import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import {
  Plus, Search, X, Trash2, Send, Calendar, Clock, User,
  ClipboardList, AlertCircle, CheckCircle2, Users, ListTodo,
  Globe, ShieldCheck, MessageSquareQuote, Eye, ChevronRight, Edit2
} from 'lucide-react';
import api from '../../services/apiService';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

const SUBJECTS_LIST = [
  { key: 'type_of_operation', label: 'Type of Operation' },
  { key: 'crane_requirements', label: 'Crane Requirements' },
  { key: 'methods_procedures', label: 'Methods & Procedures' },
  { key: 'potential_hazards', label: 'Potential Hazards' },
  { key: 'permit_to_work', label: 'Permit to Work' },
  { key: 'equipment_to_be_used', label: 'Equipment to be Used' },
  { key: 'safety_spill_equipment', label: 'Safety / Spill Equipment' },
  { key: 'materials_chemicals', label: 'Materials / Chemicals' },
  { key: 'responsibilities', label: 'Responsibilities' },
  { key: 'isolations_necessary', label: 'Isolations Necessary' },
  { key: 'access_egress', label: 'Access / Egress' },
  { key: 'impact_adjacent_work', label: 'Impact on Adjacent Work' },
  { key: 'tests_monitoring', label: 'Tests / Monitoring' },
  { key: 'environmental_hazards', label: 'Environmental Hazards' },
  { key: 'work_environment', label: 'Work Environment' },
  { key: 'waste_management', label: 'Waste Management' },
  { key: 'general_other_info', label: 'General / Other Information' },
  { key: 'flora_fauna_archeological', label: 'Flora / Fauna / Archeological' }
];

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color, bg }) => (
  <div className={`${bg} border border-slate-200 p-4 rounded-2xl shadow-sm relative overflow-hidden group hover:border-orange-500 transition-all flex items-center gap-3`}>
    <div className={`p-2.5 rounded-xl ${color} bg-slate-50 border border-slate-100 shadow-sm group-hover:bg-orange-500 group-hover:text-white transition-all`}>
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{label}</div>
      <div className={`text-xl font-black ${color} tracking-tighter group-hover:text-slate-900 transition-colors`}>{value}</div>
    </div>
  </div>
);

const OEMToolboxTalks = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  // Data states
  const [toolboxTalks, setToolboxTalks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingTalk, setViewingTalk] = useState(null);
  const [editingTalk, setEditingTalk] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProjectFilter, setSelectedProjectFilter] = useState('all');

  // Participant selection
  const [availableParticipants, setAvailableParticipants] = useState([]);
  const [participantSearch, setParticipantSearch] = useState('');

  // Custom subject
  const [newCustomSubject, setNewCustomSubject] = useState('');

  // Work activity
  const [newWorkActivity, setNewWorkActivity] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: '',
    project: '',
    topic: '',
    conductor_name: user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.username || '',
    work_activities: [],
    hazards: '',
    preventive_measures: '',
    notes: '',
    hse_comments: '',
    custom_subjects: [],
    participants: [],
    // Standard subjects (matching backend field names)
    type_of_operation: false,
    crane_requirements: false,
    methods_procedures: false,
    potential_hazards: false,
    permit_to_work: false,
    equipment_to_be_used: false,
    safety_spill_equipment: false,
    materials_chemicals: false,
    responsibilities: false,
    isolations_necessary: false,
    access_egress: false,
    impact_adjacent_work: false,
    tests_monitoring: false,
    environmental_hazards: false,
    work_environment: false,
    waste_management: false,
    general_other_info: false,
    flora_fauna_archeological: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [talksRes, projectsRes] = await Promise.all([
        api.get('/oem/toolboxes/'),
        api.get('/oem/projects/')
      ]);
      setToolboxTalks(talksRes.data.results || talksRes.data || []);
      setProjects(projectsRes.data.results || projectsRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async (projectId) => {
    if (!projectId) {
      setAvailableParticipants([]);
      return;
    }
    try {
      const response = await api.get(`/oem/projects/${projectId}/all-technicians/`);
      const technicians = response.data.technicians || [];
      setAvailableParticipants(technicians);

      // Auto-select authorized participants
      const authorizedParticipants = technicians.filter(t => t.authorization);
      if (authorizedParticipants.length > 0) {
        setFormData(prev => ({ ...prev, participants: authorizedParticipants }));
      }
    } catch (err) {
      console.error('Error fetching participants:', err);
    }
  };

  const handleProjectChange = (projectId) => {
    setFormData(prev => ({ ...prev, project: projectId, participants: [] }));
    fetchParticipants(projectId);
  };

  const toggleSubject = (key) => {
    setFormData(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const addWorkActivity = () => {
    if (newWorkActivity.trim()) {
      setFormData(prev => ({
        ...prev,
        work_activities: [...prev.work_activities, newWorkActivity.trim()]
      }));
      setNewWorkActivity('');
    }
  };

  const removeWorkActivity = (index) => {
    setFormData(prev => ({
      ...prev,
      work_activities: prev.work_activities.filter((_, i) => i !== index)
    }));
  };

  const addCustomSubject = () => {
    if (newCustomSubject.trim()) {
      setFormData(prev => ({
        ...prev,
        custom_subjects: [...prev.custom_subjects, newCustomSubject.trim()]
      }));
      setNewCustomSubject('');
    }
  };

  const toggleParticipant = (participant) => {
    setFormData(prev => {
      const exists = prev.participants.find(p => p.id === participant.id);
      if (exists) {
        return { ...prev, participants: prev.participants.filter(p => p.id !== participant.id) };
      }
      return { ...prev, participants: [...prev.participants, participant] };
    });
  };

  const handleSubmit = async (isShared = false) => {
    if (!formData.project || !formData.topic) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const payload = {
        ...formData,
        is_shared: isShared,
        participants: formData.participants.map(p => p.id)
      };
      // Remove participants array with full objects
      delete payload.participants;

      // Send with participant IDs
      const dataToSend = {
        ...payload,
        participants: formData.participants.map(p => p.id)
      };

      if (editingTalk) {
        await api.put(`/oem/toolboxes/${editingTalk.id}/`, dataToSend);
        toast.success(isShared ? 'Toolbox talk updated and shared!' : 'Toolbox talk updated!');
      } else {
        await api.post('/oem/toolboxes/', dataToSend);
        toast.success(isShared ? 'Toolbox talk created and shared!' : 'Toolbox talk saved!');
      }
      setShowModal(false);
      setEditingTalk(null);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Error saving toolbox talk:', err);
      toast.error('Failed to save toolbox talk');
    }
  };

  const handleEdit = async (talk) => {
    setEditingTalk(talk);

    // Fetch participants for the project first
    if (talk.project) {
      try {
        const response = await api.get(`/oem/projects/${talk.project}/all-technicians/`);
        const technicians = response.data.technicians || [];
        setAvailableParticipants(technicians);

        // Map participants_data to full participant objects
        const selectedParticipants = talk.participants_data?.map(pd => {
          const found = technicians.find(t => t.id === pd.id);
          return found || { id: pd.id, full_name: pd.name };
        }) || [];

        setFormData({
          date: talk.date || new Date().toISOString().split('T')[0],
          time: talk.time || '',
          project: talk.project,
          topic: talk.topic || '',
          conductor_name: talk.conductor_name || '',
          work_activities: talk.work_activities || [],
          hazards: talk.hazards || '',
          preventive_measures: talk.preventive_measures || '',
          notes: talk.notes || '',
          hse_comments: talk.hse_comments || '',
          custom_subjects: talk.custom_subjects || [],
          participants: selectedParticipants,
          type_of_operation: talk.type_of_operation || false,
          crane_requirements: talk.crane_requirements || false,
          methods_procedures: talk.methods_procedures || false,
          potential_hazards: talk.potential_hazards || false,
          permit_to_work: talk.permit_to_work || false,
          equipment_to_be_used: talk.equipment_to_be_used || false,
          safety_spill_equipment: talk.safety_spill_equipment || false,
          materials_chemicals: talk.materials_chemicals || false,
          responsibilities: talk.responsibilities || false,
          isolations_necessary: talk.isolations_necessary || false,
          access_egress: talk.access_egress || false,
          impact_adjacent_work: talk.impact_adjacent_work || false,
          tests_monitoring: talk.tests_monitoring || false,
          environmental_hazards: talk.environmental_hazards || false,
          work_environment: talk.work_environment || false,
          waste_management: talk.waste_management || false,
          general_other_info: talk.general_other_info || false,
          flora_fauna_archeological: talk.flora_fauna_archeological || false
        });
      } catch (err) {
        console.error('Error fetching participants:', err);
      }
    }

    setShowViewModal(false);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this toolbox talk?')) return;
    try {
      await api.delete(`/oem/toolboxes/${id}/`);
      toast.success('Toolbox talk deleted');
      setShowViewModal(false);
      fetchData();
    } catch (err) {
      console.error('Error deleting toolbox talk:', err);
      toast.error('Failed to delete toolbox talk');
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      time: '',
      project: '',
      topic: '',
      conductor_name: user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.username || '',
      work_activities: [],
      hazards: '',
      preventive_measures: '',
      notes: '',
      hse_comments: '',
      custom_subjects: [],
      participants: [],
      type_of_operation: false,
      crane_requirements: false,
      methods_procedures: false,
      potential_hazards: false,
      permit_to_work: false,
      equipment_to_be_used: false,
      safety_spill_equipment: false,
      materials_chemicals: false,
      responsibilities: false,
      isolations_necessary: false,
      access_egress: false,
      impact_adjacent_work: false,
      tests_monitoring: false,
      environmental_hazards: false,
      work_environment: false,
      waste_management: false,
      general_other_info: false,
      flora_fauna_archeological: false
    });
    setAvailableParticipants([]);
    setEditingTalk(null);
  };

  const filteredTalks = toolboxTalks.filter(talk => {
    if (selectedProjectFilter !== 'all' && talk.project !== parseInt(selectedProjectFilter)) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return talk.topic?.toLowerCase().includes(term) || talk.conductor_name?.toLowerCase().includes(term);
    }
    return true;
  });

  const stats = {
    total: toolboxTalks.length,
    thisWeek: toolboxTalks.filter(t => {
      const talkDate = new Date(t.date);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return talkDate >= weekAgo;
    }).length,
    shared: toolboxTalks.filter(t => t.is_shared).length,
    completion: toolboxTalks.length > 0
      ? Math.round((toolboxTalks.filter(t => t.is_shared).length / toolboxTalks.length) * 100)
      : 0
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const filteredParticipants = availableParticipants
    .filter(p => {
      if (!participantSearch) return true;
      const term = participantSearch.toLowerCase();
      return p.full_name?.toLowerCase().includes(term) || p.email?.toLowerCase().includes(term);
    })
    .sort((a, b) => {
      // Sort authorized participants to the top
      const aAuth = a.authorization ? 1 : 0;
      const bAuth = b.authorization ? 1 : 0;
      if (bAuth !== aAuth) return bAuth - aAuth;
      // Then by name
      return (a.full_name || '').localeCompare(b.full_name || '');
    });

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm">Loading toolbox talks...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Action Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-500 transition-all shadow-xl shadow-slate-900/10"
        >
          <Plus className="h-4 w-4" /> New Toolbox Talk
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total Talks" value={stats.total} icon={ClipboardList} color="text-slate-900" bg="bg-white" />
        <StatCard label="This Week" value={stats.thisWeek} icon={Calendar} color="text-orange-500" bg="bg-white" />
        <StatCard label="Shared" value={stats.shared} icon={Send} color="text-emerald-500" bg="bg-white" />
        <StatCard label="Completion Rate" value={`${stats.completion}%`} icon={CheckCircle2} color="text-slate-900" bg="bg-white" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 h-4 w-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search toolbox talks..."
              className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-orange-500/10 outline-none transition-all"
            />
          </div>
          <select
            value={selectedProjectFilter}
            onChange={(e) => setSelectedProjectFilter(e.target.value)}
            className="pl-4 pr-8 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[11px] font-bold uppercase tracking-widest text-slate-600 outline-none"
          >
            <option value="all">All Projects</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.code} - {p.name}</option>
            ))}
          </select>
        </div>

        {/* List */}
        {filteredTalks.length === 0 ? (
          <div className="p-16 flex flex-col items-center text-center">
            <div className="w-20 h-20 bg-slate-50 rounded-[32px] flex items-center justify-center text-slate-200 mb-6">
              <ListTodo className="h-10 w-10" />
            </div>
            <h3 className="text-lg font-black text-slate-900 tracking-tight uppercase">History Ledger Empty</h3>
            <p className="text-slate-500 font-medium max-w-sm mt-2 text-sm">Start your shift by creating the first toolbox talk for your active project.</p>
            <button
              onClick={() => setShowModal(true)}
              className="mt-6 px-6 py-2.5 bg-orange-50 text-orange-500 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-100 transition-all"
            >
              Create Briefing
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {filteredTalks.map(talk => (
              <div
                key={talk.id}
                onClick={() => { setViewingTalk(talk); setShowViewModal(true); }}
                className="p-4 hover:bg-slate-50/80 transition-all cursor-pointer flex items-center gap-4"
              >
                <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center">
                  <MessageSquareQuote className="h-5 w-5 text-orange-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-black text-slate-900">{talk.topic}</h4>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${
                      talk.is_shared ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {talk.is_shared ? 'Shared' : 'Draft'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1">
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                      {projects.find(p => p.id === talk.project)?.code || 'N/A'}
                    </span>
                    <span className="text-[10px] text-slate-400">{formatDate(talk.date)}</span>
                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                      <Users className="h-3 w-3" /> {talk.participants_count || 0}
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-xl">
          <div className="bg-white w-full max-w-6xl h-full max-h-[90vh] rounded-[40px] shadow-2xl overflow-hidden flex flex-col border border-white/20">
            {/* Modal Header */}
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-20">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-500 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-orange-500/20">
                  <MessageSquareQuote className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                    {editingTalk ? 'Edit Toolbox Talk' : 'New Toolbox Talk'}
                  </h3>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Operational Safety Briefing System</p>
                </div>
              </div>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="w-10 h-10 flex items-center justify-center bg-slate-50 hover:bg-rose-50 text-slate-400 hover:text-rose-500 rounded-xl transition-all">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-hidden flex bg-slate-50/30">
              {/* Left Column - Form */}
              <div className="flex-1 overflow-y-auto p-8 space-y-8">
              {/* Basic Info */}
              <div className="grid grid-cols-4 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Project *</label>
                  <div className="relative">
                    <select
                      value={formData.project}
                      onChange={(e) => handleProjectChange(e.target.value)}
                      className="w-full pl-4 pr-10 py-3 bg-white border border-slate-200 rounded-xl text-[11px] font-bold appearance-none focus:ring-4 focus:ring-orange-500/10 outline-none"
                    >
                      <option value="">Select project...</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
                    </select>
                    <Globe className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none h-4 w-4" />
                  </div>
                </div>
                <div className="col-span-2 space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Topic *</label>
                  <input
                    type="text"
                    value={formData.topic}
                    onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                    placeholder="e.g., Morning Safety Briefing"
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-[11px] font-bold focus:ring-4 focus:ring-orange-500/10 outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-[11px] font-bold focus:ring-4 focus:ring-orange-500/10 outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Time</label>
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-[11px] font-bold focus:ring-4 focus:ring-orange-500/10 outline-none"
                  />
                </div>
                <div className="col-span-3 space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Conductor/Lead</label>
                  <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl">
                    <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600">
                      <User className="h-4 w-4" />
                    </div>
                    <span className="text-[11px] font-bold text-slate-900 uppercase">{formData.conductor_name}</span>
                  </div>
                </div>
              </div>

              {/* Work Activities */}
              <div className="space-y-3">
                <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-900 border-l-4 border-orange-500 pl-3">Work Activities / Tasks</h4>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newWorkActivity}
                    onChange={(e) => setNewWorkActivity(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addWorkActivity()}
                    placeholder="Type activity and press Enter..."
                    className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-xl text-[11px] font-bold focus:ring-4 focus:ring-orange-500/10 outline-none"
                  />
                  <button onClick={addWorkActivity} className="px-5 py-3 bg-slate-100 rounded-xl text-[10px] font-black uppercase text-slate-500 hover:bg-slate-200">Add</button>
                </div>
                {formData.work_activities.length === 0 ? (
                  <div className="p-8 border-2 border-dashed border-slate-200 rounded-2xl text-center text-slate-400 text-[10px] font-black uppercase tracking-widest">
                    No activities added yet
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {formData.work_activities.map((activity, idx) => (
                      <span key={idx} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl text-[10px] font-bold text-slate-700">
                        {activity}
                        <button onClick={() => removeWorkActivity(idx)} className="text-slate-400 hover:text-rose-500"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Subjects Discussed */}
              <div className="space-y-3">
                <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-900 border-l-4 border-orange-500 pl-3">Subjects Discussed</h4>
                <div className="grid grid-cols-3 gap-3 p-6 bg-white border border-slate-100 rounded-[32px] shadow-sm">
                  {SUBJECTS_LIST.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => toggleSubject(key)}
                      className={`flex items-center gap-2 p-3 rounded-xl border text-[10px] font-bold text-left transition-all ${
                        formData[key]
                          ? 'bg-orange-500 border-orange-600 text-white shadow-lg shadow-orange-500/20'
                          : 'bg-slate-50 border-slate-100 text-slate-500 hover:border-slate-300'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-md border flex items-center justify-center ${formData[key] ? 'bg-white border-white' : 'bg-white border-slate-200'}`}>
                        {formData[key] && <CheckCircle2 className="h-3 w-3 text-orange-500" />}
                      </div>
                      {label}
                    </button>
                  ))}
                  {formData.custom_subjects.map((subject, idx) => (
                    <div key={`custom-${idx}`} className="flex items-center gap-2 p-3 rounded-xl border bg-orange-500 border-orange-600 text-white shadow-lg shadow-orange-500/20 text-[10px] font-bold">
                      <CheckCircle2 className="h-4 w-4" />
                      {subject}
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 items-center pt-2">
                  <input
                    type="text"
                    value={newCustomSubject}
                    onChange={(e) => setNewCustomSubject(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addCustomSubject()}
                    placeholder="Add custom subject..."
                    className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-xl text-[10px] font-bold outline-none"
                  />
                  <button onClick={addCustomSubject} className="px-5 py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase">Add Custom</button>
                </div>
              </div>

              {/* Hazards & Measures */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-900 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-orange-500" /> Hazards
                  </h4>
                  <textarea
                    value={formData.hazards}
                    onChange={(e) => setFormData(prev => ({ ...prev, hazards: e.target.value }))}
                    placeholder="List potential hazards discussed..."
                    className="w-full h-32 p-4 bg-white border border-slate-200 rounded-2xl text-[11px] font-medium focus:ring-4 focus:ring-orange-500/10 outline-none resize-none"
                  />
                </div>
                <div className="space-y-3">
                  <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-900 flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-500" /> Preventive Measures
                  </h4>
                  <textarea
                    value={formData.preventive_measures}
                    onChange={(e) => setFormData(prev => ({ ...prev, preventive_measures: e.target.value }))}
                    placeholder="List preventive measures and controls..."
                    className="w-full h-32 p-4 bg-white border border-slate-200 rounded-2xl text-[11px] font-medium focus:ring-4 focus:ring-orange-500/10 outline-none resize-none"
                  />
                </div>
              </div>

              {/* Notes & HSE Comments */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes..."
                    className="w-full h-28 p-4 bg-white border border-slate-200 rounded-2xl text-[11px] font-medium focus:ring-4 focus:ring-orange-500/10 outline-none resize-none"
                  />
                </div>
                <div className="space-y-3 p-4 bg-orange-50 rounded-2xl border border-orange-100">
                  <label className="text-[10px] font-black text-orange-600 uppercase tracking-widest px-1">HSE Comments</label>
                  <textarea
                    value={formData.hse_comments}
                    onChange={(e) => setFormData(prev => ({ ...prev, hse_comments: e.target.value }))}
                    placeholder="HSE manager comments..."
                    className="w-full h-20 p-4 bg-white border border-orange-200 rounded-xl text-[11px] font-medium outline-none resize-none"
                  />
                </div>
              </div>
              </div>

              {/* Right Column - Participants */}
              <div className="w-[340px] border-l border-slate-200 bg-white flex flex-col">
                <div className="p-5 border-b border-slate-100">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-900 flex items-center gap-2">
                      <Users className="h-4 w-4 text-orange-500" />
                      Participants ({formData.participants.length})
                    </h4>
                    {formData.project && availableParticipants.length > 0 && (
                      <button
                        onClick={() => {
                          if (formData.participants.length === availableParticipants.length) {
                            setFormData(prev => ({ ...prev, participants: [] }));
                          } else {
                            setFormData(prev => ({ ...prev, participants: [...availableParticipants] }));
                          }
                        }}
                        className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wide transition-all ${
                          formData.participants.length === availableParticipants.length
                            ? 'bg-orange-500 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {formData.participants.length === availableParticipants.length ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 h-3.5 w-3.5" />
                    <input
                      type="text"
                      value={participantSearch}
                      onChange={(e) => setParticipantSearch(e.target.value)}
                      placeholder="Search participants..."
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 text-[10px] font-medium focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  {formData.project ? (
                    filteredParticipants.length > 0 ? (
                      <div className="space-y-2">
                        {filteredParticipants.map((p) => {
                          const isSelected = formData.participants.find(pp => pp.id === p.id);
                          return (
                            <button
                              key={p.id}
                              onClick={() => toggleParticipant(p)}
                              className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                                isSelected
                                  ? 'bg-orange-50 border-orange-400 shadow-sm'
                                  : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                              }`}
                            >
                              {p.profile_picture ? (
                                <img src={getImageUrl(p.profile_picture)} alt={p.full_name} className="w-9 h-9 rounded-lg object-cover" />
                              ) : (
                                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-black text-[10px]">
                                  {p.full_name?.charAt(0) || 'U'}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[11px] font-black text-slate-900 truncate">{p.full_name}</span>
                                  {p.authorization && (
                                    <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-600 text-[8px] font-black rounded uppercase">
                                      {p.authorization.level_code}
                                    </span>
                                  )}
                                </div>
                                <div className="text-[9px] text-slate-400 font-bold uppercase truncate">{p.company_name}</div>
                              </div>
                              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                                isSelected ? 'bg-orange-500 border-orange-500' : 'bg-white border-slate-300'
                              }`}>
                                {isSelected && <CheckCircle2 className="h-3 w-3 text-white" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-center p-6">
                        <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-3">
                          <Search className="h-5 w-5 text-slate-300" />
                        </div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">No participants found</p>
                      </div>
                    )
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-center p-6">
                      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-3">
                        <Users className="h-5 w-5 text-slate-300" />
                      </div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Select a project first</p>
                      <p className="text-[9px] text-slate-300 mt-1">Participants will appear here</p>
                    </div>
                  )}
                </div>

                {/* Selected count footer */}
                {formData.participants.length > 0 && (
                  <div className="p-4 border-t border-slate-100 bg-orange-50">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-orange-600 uppercase tracking-wide">
                        {formData.participants.length} Selected
                      </span>
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, participants: [] }))}
                        className="text-[9px] font-bold text-orange-500 hover:text-orange-600 uppercase"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-slate-100 bg-white flex justify-end gap-3">
              <button onClick={() => { setShowModal(false); resetForm(); }} className="px-6 py-3 bg-slate-100 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all">
                Cancel
              </button>
              <button onClick={() => handleSubmit(false)} className="px-8 py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-orange-500 transition-all">
                {editingTalk ? 'Update Draft' : 'Save Draft'}
              </button>
              <button onClick={() => handleSubmit(true)} className="px-8 py-3 bg-orange-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-orange-600 transition-all flex items-center gap-2">
                <Send className="h-4 w-4" /> Finalize & Share
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingTalk && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-xl">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-[32px] shadow-2xl overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                  <MessageSquareQuote className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-black text-slate-900">{viewingTalk.topic}</h3>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${
                      viewingTalk.is_shared ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {viewingTalk.is_shared ? 'Shared' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                    {formatDate(viewingTalk.date)} {viewingTalk.time && `• ${viewingTalk.time}`}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowViewModal(false)} className="w-9 h-9 flex items-center justify-center bg-slate-50 hover:bg-rose-50 text-slate-400 hover:text-rose-500 rounded-xl transition-all">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Project</p>
                  <p className="text-sm font-bold text-slate-900">{projects.find(p => p.id === viewingTalk.project)?.name || 'N/A'}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Conductor</p>
                  <p className="text-sm font-bold text-slate-900">{viewingTalk.conductor_display_name || viewingTalk.conductor_name || 'N/A'}</p>
                </div>
              </div>

              {/* Work Activities */}
              {viewingTalk.work_activities?.length > 0 && (
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Work Activities / Tasks</p>
                  <div className="flex flex-wrap gap-2">
                    {viewingTalk.work_activities.map((activity, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-white rounded-lg text-xs font-bold text-slate-700 border border-slate-200">
                        {activity}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Subjects Discussed */}
              {(() => {
                const selectedSubjects = SUBJECTS_LIST.filter(s => viewingTalk[s.key]);
                const customSubjects = viewingTalk.custom_subjects || [];
                if (selectedSubjects.length > 0 || customSubjects.length > 0) {
                  return (
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <p className="text-[9px] font-black text-blue-600 uppercase tracking-widest mb-2">Subjects Discussed</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedSubjects.map((subject, idx) => (
                          <span key={idx} className="px-3 py-1.5 bg-blue-100 rounded-lg text-xs font-bold text-blue-700">
                            {subject.label}
                          </span>
                        ))}
                        {customSubjects.map((subject, idx) => (
                          <span key={`custom-${idx}`} className="px-3 py-1.5 bg-violet-100 rounded-lg text-xs font-bold text-violet-700">
                            {subject}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Hazards & Preventive Measures */}
              <div className="grid grid-cols-2 gap-4">
                {viewingTalk.hazards && (
                  <div className="p-4 bg-orange-50 rounded-xl border border-orange-100">
                    <p className="text-[9px] font-black text-orange-600 uppercase tracking-widest mb-2">Hazards</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{viewingTalk.hazards}</p>
                  </div>
                )}
                {viewingTalk.preventive_measures && (
                  <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                    <p className="text-[9px] font-black text-emerald-600 uppercase tracking-widest mb-2">Preventive Measures</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{viewingTalk.preventive_measures}</p>
                  </div>
                )}
              </div>

              {/* Notes & HSE Comments */}
              <div className="grid grid-cols-2 gap-4">
                {viewingTalk.notes && (
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Notes</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{viewingTalk.notes}</p>
                  </div>
                )}
                {viewingTalk.hse_comments && (
                  <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                    <p className="text-[9px] font-black text-amber-600 uppercase tracking-widest mb-2">HSE Comments</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{viewingTalk.hse_comments}</p>
                  </div>
                )}
              </div>

              {/* Participants */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Participants ({viewingTalk.participants_count || 0})</p>
                <div className="flex flex-wrap gap-2">
                  {viewingTalk.participants_data?.length > 0
                    ? viewingTalk.participants_data.map((p, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-white rounded-lg text-xs font-bold text-slate-700 border border-slate-200">{p.name}</span>
                      ))
                    : <span className="text-sm text-slate-400">No participants</span>
                  }
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-between">
              <div className="flex gap-2">
                {!viewingTalk.is_shared && (
                  <>
                    <button
                      onClick={() => handleEdit(viewingTalk)}
                      className="px-5 py-2.5 bg-orange-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-all flex items-center gap-2"
                    >
                      <Edit2 className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(viewingTalk.id)}
                      className="px-5 py-2.5 bg-red-50 text-red-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-100 transition-all flex items-center gap-2"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </>
                )}
              </div>
              <button onClick={() => setShowViewModal(false)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OEMToolboxTalks;
